import type { ColumnType } from 'antd/es/table';

import { sortByAmount, sortByTime } from 'utils/antd/sorters';
import { formatDecimalAmount } from 'utils/formatters/formatCurrency';

import type { Order } from 'api';

import { COLLECTION_ORDER_STATUSES } from 'models/order';

import type { FindAccountByAccountNumber } from 'hooks/context/useVirtualAccounts';

import { ColumnTitle } from '@atoms/ColumnTitle';
import { TransactionIconMinus } from '@atoms/Icon/TransactionIconMinus';
import { StatusTag } from '@atoms/StatusTag';

import { FormattedDateTime } from '@molecules/FormattedDateTime';

export function getColumns(
  findAccountByAccountNumber: FindAccountByAccountNumber
): ColumnType<Order>[] {
  return [
    {
      dataIndex: 'icon',
      key: 'icon',
      width: 100,
      render: () => (
        <div className="flex justify-center">
          <TransactionIconMinus width={32} height={32} />
        </div>
      )
    },
    {
      title: <ColumnTitle title="Date" />,
      dataIndex: 'created_at',
      key: 'created_at',
      render: (createdAt: string) => <FormattedDateTime value={createdAt} />,
      sorter: (a, b) => sortByTime(a.created_at, b.created_at),
      onHeaderCell: () => ({
        className: 'custom-table-header-cell'
      })
    },
    {
      title: <ColumnTitle title="ID/Reference" />,
      dataIndex: 'unique_reference',
      key: 'unique_reference',
      width: 200,
      render: (_uuid: string, record) => (
        <div>
          <p className="text-xs">{record?.uuid?.toLocaleUpperCase()}</p>
          <div className="text-xs font-semibold">
            {record?.reference?.toUpperCase()}
          </div>
        </div>
      ),
      onHeaderCell: () => ({
        className: 'custom-table-header-cell'
      })
    },
    {
      title: <ColumnTitle title="Account" />,
      dataIndex: 'virtual_account_number',
      key: 'virtual_account_number',
      align: 'right',
      render: (virtualAccountNumber: string) => {
        const account = findAccountByAccountNumber(virtualAccountNumber);
        return (
          <div>
            <p className="text-sm">{virtualAccountNumber}</p>
            <div className="font-semibold">{account?.name}</div>
          </div>
        );
      }
    },
    {
      title: <ColumnTitle title="Amount" />,
      dataIndex: 'amount',
      key: 'amount',
      align: 'right',
      sorter: (a, b) => sortByAmount(a.amount, b.amount),
      render: (amount: number, record) => {
        const formattedAmount = formatDecimalAmount(amount);
        return (
          <div>
            <p className="text-sm">{record.currency_code}</p>
            <div className="font-semibold">{formattedAmount}</div>
          </div>
        );
      }
    },
    {
      title: <ColumnTitle title="Status" />,
      dataIndex: 'status',
      key: 'status',
      align: 'right',
      render: (status: string) => {
        return (
          <StatusTag status={status} statuses={COLLECTION_ORDER_STATUSES} />
        );
      }
    }
  ];
}
